import { useEffect, useState } from 'react';
import { prefersReducedMotion, useCountUp, useInView } from '../hooks';
import { PNL_STRATEGY } from './desktopData';
import './PnlLedger.css';

const ROW_STAGGER_MS = 260;

function fracToNumber(odds) {
  const [n, d] = String(odds).split('/').map(Number);
  return d ? n / d : Number(odds) - 1;
}

/* Same settlement as the app's P&L Tracker: an each-way stake is split across win and place, place part at 1/5 odds. */
export function settle(pick, stake) {
  const odds = fracToNumber(pick.odds);
  const isEW = pick.type === 'ew';
  const part = isEW ? stake / 2 : stake;
  const won = pick.pos === 1;
  const placed = isEW && pick.pos > 0 && pick.pos <= pick.placesPaid;
  let ret = 0;
  if (won) ret += part * (odds + 1);
  if (placed) ret += part * (odds / 5 + 1);
  return { outlay: stake, ret, pnl: ret - stake };
}

const money = (value) => `£${Math.abs(value).toFixed(2)}`;
const signed = (value) => `${value >= 0 ? '+' : '−'}${money(value)}`;
const ordinal = (n) => {
  if (!n) return 'U';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

function Sparkline({ points, run }) {
  const w = 220;
  const h = 64;
  const min = Math.min(0, ...points);
  const max = Math.max(0, ...points);
  const span = max - min || 1;
  const x = (i) => (i / Math.max(1, points.length - 1)) * w;
  const y = (v) => h - ((v - min) / span) * h;
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <svg className={`pnl-spark${run ? ' is-drawn' : ''}${last < 0 ? ' is-loss' : ''}`} viewBox={`-4 -6 ${w + 8} ${h + 12}`} aria-hidden="true">
      <line x1="0" x2={w} y1={y(0)} y2={y(0)} className="pnl-spark__zero" />
      <path d={d} className="pnl-spark__line" pathLength="1" />
      <circle cx={x(points.length - 1)} cy={y(last)} r="4" className="pnl-spark__dot" />
    </svg>
  );
}

/* Results view of an example strategy, modelled on the app's Profit & Loss Tracker. */
function PnlLedger({ dense = false, className = '' }) {
  const { picks, stake, name } = PNL_STRATEGY;
  const rows = picks.map((pick) => ({ pick, ...settle(pick, stake) }));
  const totals = rows.reduce((acc, row) => ({ outlay: acc.outlay + row.outlay, ret: acc.ret + row.ret, pnl: acc.pnl + row.pnl }), { outlay: 0, ret: 0, pnl: 0 });
  const cumulative = rows.reduce((acc, row) => [...acc, (acc[acc.length - 1] || 0) + row.pnl], [0]);

  const [ref, inView] = useInView({ threshold: 0.4 });
  const [shown, setShown] = useState(0);
  const settled = shown >= rows.length;

  useEffect(() => {
    if (!inView) return undefined;
    if (prefersReducedMotion()) {
      setShown(rows.length);
      return undefined;
    }
    const timers = rows.map((_, i) => window.setTimeout(() => setShown(i + 1), 300 + i * ROW_STAGGER_MS));
    return () => timers.forEach(window.clearTimeout);
  }, [inView, rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const outlay = useCountUp(totals.outlay, settled, 900);
  const ret = useCountUp(totals.ret, settled, 900);
  const pnl = useCountUp(totals.pnl, settled, 1100);

  return (
    <div ref={ref} className={`pnl${dense ? ' pnl--dense' : ''} ${className}`.trim()}>
      <div className="pnl__head">
        <div>
          <span className="pnl__kicker">Profit &amp; Loss Tracker</span>
          <strong className="pnl__strategy">{name} strategy</strong>
        </div>
        <div className="pnl__stake">
          <span>Default stake</span>
          <strong>£{stake}</strong>
        </div>
      </div>

      <ol className="pnl__rows">
        {rows.map(({ pick, pnl: rowPnl }, i) => (
          <li key={`${pick.horse}-${pick.time}`} className={`pnl__row${i < shown ? ' is-in' : ''}`}>
            <img src={pick.silk} alt="" className="pnl__silk" />
            <div className="pnl__who">
              <strong>{pick.horse}</strong>
              <span>{pick.time} {pick.course}</span>
            </div>
            <span className={`pnl__type pnl__type--${pick.type}`}>{pick.type === 'ew' ? 'Each Way' : 'Win'}</span>
            <span className="pnl__odds">
              <small>{pick.oddsSource}</small>
              {pick.odds}
            </span>
            <span className={`pnl__pos${pick.pos === 1 ? ' is-win' : pick.type === 'ew' && pick.pos && pick.pos <= pick.placesPaid ? ' is-place' : ''}`}>
              {ordinal(pick.pos)}
            </span>
            <span className={`pnl__pl ${rowPnl >= 0 ? 'is-profit' : 'is-loss'}`}>{signed(rowPnl)}</span>
          </li>
        ))}
      </ol>

      <div className={`pnl__totals ${totals.pnl >= 0 ? 'is-profit' : 'is-loss'}`}>
        <div className="pnl__total">
          <span>Total Outlay</span>
          <strong>{money(outlay)}</strong>
        </div>
        <div className="pnl__total">
          <span>Total Returns</span>
          <strong>{money(ret)}</strong>
        </div>
        <div className="pnl__total pnl__total--pnl">
          <span>Overall P&amp;L</span>
          <strong>{signed(pnl)}</strong>
        </div>
        <Sparkline points={cumulative} run={settled} />
      </div>
    </div>
  );
}

export const PNL_FOOTNOTE = 'Example strategy tracked in HorseStat. Past results don’t guarantee future returns.';

export default PnlLedger;
